import React from 'react';

const ico = {
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
};

export type NavIconId =
    | 'dashboard'
    | 'clients'
    | 'jobs'
    | 'quotes'
    | 'invoices'
    | 'settings'
    | 'jobList'
    | 'jobLog'
    | 'timeSheet'
    | 'reports'
    | 'inspections'
    | 'deficiencies'
    | 'more';

type NavIconProps = {
    id: NavIconId;
    className?: string;
};

/** Compact inline SVGs (24×24) — decorative; parent control provides the accessible name. */
export function NavIcon({ id, className }: NavIconProps): React.JSX.Element {
    const wrap = (children: React.ReactNode): React.JSX.Element => (
        <svg width={24} height={24} viewBox="0 0 24 24" className={className} aria-hidden>
            {children}
        </svg>
    );

    switch (id) {
        case 'dashboard':
            return wrap(<path {...ico} d="M4 10.5 12 3l8 7.5V20a1 1 0 0 1-1 1h-4v-7H9v7H5a1 1 0 0 1-1-1v-9.5Z" />);
        case 'clients':
            return wrap(
                <>
                    <circle {...ico} cx={9} cy={7} r={3.5} />
                    <path {...ico} d="M3 19.5v-.5a5.5 5.5 0 0 1 5.5-5.5h1a5.5 5.5 0 0 1 5.5 5.5v.5" />
                    <circle {...ico} cx={17} cy={8.5} r={2.75} />
                    <path {...ico} d="M21 19.5v-.3a3.2 3.2 0 0 0-2.2-3" />
                </>
            );
        case 'jobs':
            return wrap(
                <>
                    <path {...ico} d="M9 4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3H9V4Z" />
                    <path {...ico} d="M6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                    <path {...ico} d="M9 12.5h6M9 16h4" />
                </>
            );
        case 'quotes':
            return wrap(
                <>
                    <path {...ico} d="M7 3h8l3 3v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
                    <path {...ico} d="M9 8.5h6M9 12.5h6M9 16.5h4" />
                </>
            );
        case 'invoices':
            return wrap(
                <>
                    <path {...ico} d="M7 3h10a1 1 0 0 1 1 1v16l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5V4a1 1 0 0 1 1-1Z" />
                    <path {...ico} d="M9 8.5h6M9 12.5h5" />
                    <path {...ico} d="M9 16.5h3" strokeWidth={2.25} />
                </>
            );
        case 'settings':
            return wrap(
                <>
                    <circle {...ico} cx={12} cy={12} r={3.25} />
                    <path
                        {...ico}
                        d="M19.2 13.1v-2.2l-1.9-.5a5.7 5.7 0 0 0-.6-1.4l1.1-1.7-1.6-1.6-1.7 1.1a5.7 5.7 0 0 0-1.4-.6L13.1 4h-2.2l-.5 1.9a5.7 5.7 0 0 0-1.4.6L7.3 5.4 5.7 7l1.1 1.7a5.7 5.7 0 0 0-.6 1.4L4.3 10.9v2.2l1.9.5a5.7 5.7 0 0 0 .6 1.4l-1.1 1.7 1.6 1.6 1.7-1.1a5.7 5.7 0 0 0 1.4.6l.5 1.9h2.2l.5-1.9a5.7 5.7 0 0 0 1.4-.6l1.7 1.1 1.6-1.6-1.1-1.7a5.7 5.7 0 0 0 .6-1.4l1.9-.5Z"
                    />
                </>
            );
        case 'jobList':
            return wrap(
                <>
                    <path {...ico} d="M8 6.5h12M8 12h12M8 17.5h12" />
                    <path {...ico} d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" strokeWidth={2.5} />
                </>
            );
        case 'jobLog':
            return wrap(
                <>
                    <path {...ico} d="M8 3h8l2 2v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
                    <path {...ico} d="M9 9h6M9 13h6M9 17h4" />
                </>
            );
        case 'timeSheet':
            return wrap(
                <>
                    <circle {...ico} cx={12} cy={12} r={8.5} />
                    <path {...ico} d="M12 7v6l4 2" />
                </>
            );
        case 'reports':
            return wrap(
                <>
                    <path {...ico} d="M4 20V4a1 1 0 0 1 1-1h5l2 2h7a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z" />
                    <path {...ico} d="M8 14v5M12 11v8M16 15v4" />
                </>
            );
        case 'inspections':
            return wrap(
                <>
                    <path {...ico} d="M9 5H7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                    <path {...ico} d="M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2Z" />
                    <path {...ico} d="M9 12l2 2 4-4" />
                </>
            );
        case 'deficiencies':
            return wrap(
                <>
                    <path {...ico} d="M12 9v4M12 17h.01" />
                    <path {...ico} d="M10.3 3.3 2.2 18.4a1 1 0 0 0 .9 1.4h17.8a1 1 0 0 0 .9-1.4L13.7 3.3a1 1 0 0 0-1.8 0Z" />
                </>
            );
        case 'more':
            return wrap(
                <>
                    <circle {...ico} cx={7} cy={12} r={1.25} fill="currentColor" stroke="none" />
                    <circle {...ico} cx={12} cy={12} r={1.25} fill="currentColor" stroke="none" />
                    <circle {...ico} cx={17} cy={12} r={1.25} fill="currentColor" stroke="none" />
                </>
            );
        default: {
            const _n: never = id;
            return _n;
        }
    }
}
